import { useState } from 'react';
import { ArrowRight, MessageCircle, Award, Shield, BookOpen, Users, CheckCircle, Star, Zap, Globe, TrendingUp } from 'lucide-react';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import SEOHead from '@/components/SEOHead';
import { DemoModal } from '@/components/DemoModal';

var PARTNER_TIERS = [
  {
    name: "Registered Partner",
    color: "#2C39B8",
    benefits: ["Access to ChannelLoyalty partner portal", "Co-branded marketing assets", "Lead registration & protection", "Partner community access"],
    requirements: ["Complete partner application", "Attend onboarding webinar"],
  },
  {
    name: "Certified Partner",
    color: "#6FC8F5",
    benefits: ["Everything in Registered +", "Priority lead routing", "Dedicated partner manager", "Revenue share on referred deals", "Joint go-to-market campaigns"],
    requirements: ["Complete ChannelLoyalty certification", "2+ successful implementations", "Quarterly business review"],
  },
  {
    name: "Premium Partner",
    color: "#7CC326",
    benefits: ["Everything in Certified +", "Executive sponsorship", "Custom pricing models", "Priority product roadmap input", "Annual partner summit access", "Exclusive territory rights"],
    requirements: ["5+ active client deployments", "Dedicated ChannelLoyalty practice team", "Annual revenue commitment"],
  },
];

var CERT_MODULES = [
  { icon: BookOpen, title: "B2B Loyalty Foundations", desc: "Channel loyalty architecture, multi-tier ecosystems, and B2B vs B2C differences.", duration: "4 hours" },
  { icon: Zap, title: "ChannelLoyalty Platform Mastery", desc: "Complete platform training — program builder, rewards catalog, analytics, and integrations.", duration: "8 hours" },
  { icon: Users, title: "Implementation Methodology", desc: "End-to-end deployment playbook — scoping, configuration, testing, go-live, and optimization.", duration: "6 hours" },
  { icon: TrendingUp, title: "AI & Analytics Deep Dive", desc: "Churn prediction, scheme optimization, fraud detection, and ROI measurement models.", duration: "4 hours" },
  { icon: Globe, title: "Industry Specialization", desc: "Vertical-specific loyalty strategies for Cement, FMCG, Pharma, Auto, Paints, and Electrical.", duration: "6 hours" },
  { icon: Shield, title: "Compliance & Governance", desc: "TDS automation, GST compliance, data privacy, audit trails, and regulatory frameworks.", duration: "3 hours" },
];

// Pre-render lists to avoid nested .map() Babel crash
function BenefitsList({ benefits, color }) {
  var items = [];
  for (var i = 0; i < benefits.length; i++) {
    items.push(
      <li key={i} style={{ fontSize: '14px', color: '#334155', marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <CheckCircle size={15} color={color} style={{ flexShrink: 0, marginTop: '2px' }} />
        <span>{benefits[i]}</span>
      </li>
    );
  }
  return <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>{items}</ul>;
}

function RequirementsList({ requirements }) {
  var items = [];
  for (var i = 0; i < requirements.length; i++) {
    items.push(
      <li key={i} style={{ fontSize: '14px', color: '#64748b', marginBottom: '6px', paddingLeft: '20px', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 0 }}>-</span>{requirements[i]}
      </li>
    );
  }
  return <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>{items}</ul>;
}

function PartnerTierCard({ tier, idx }) {
  return (
    <div className="card" style={{ border: '2px solid ' + tier.color + '22', position: 'relative', overflow: 'hidden' }} data-testid={"partner-tier-" + idx}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: tier.color }}></div>
      <div style={{ padding: '32px' }}>
        <Star size={24} color={tier.color} />
        <h3 style={{ fontSize: '20px', fontWeight: 700, margin: '12px 0 20px', color: '#1e293b' }}>{tier.name}</h3>
        <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '12px', letterSpacing: '1px' }}>Benefits</h4>
        <BenefitsList benefits={tier.benefits} color={tier.color} />
        <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '12px', letterSpacing: '1px' }}>Requirements</h4>
        <RequirementsList requirements={tier.requirements} />
      </div>
    </div>
  );
}

var API_URL = process.env.REACT_APP_BACKEND_URL;

function PartnerApplyForm() {
  var formState = useState({ name: '', company: '', email: '', phone: '', message: '' });
  var form = formState[0];
  var setForm = formState[1];
  var statusState = useState('idle'); // idle | sending | done | error
  var status = statusState[0];
  var setStatus = statusState[1];

  function update(field) {
    return function(e) {
      var next = Object.assign({}, form);
      next[field] = e.target.value;
      setForm(next);
    };
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) return;
    setStatus('sending');
    try {
      var res = await fetch(API_URL + '/api/partners/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('failed');
      setStatus('done');
    } catch (err) {
      setStatus('error');
    }
  }

  if (status === 'done') {
    return (
      <div className="bg-white rounded-2xl p-8 text-center max-w-lg mx-auto shadow-xl" data-testid="partner-apply-success">
        <CheckCircle size={44} color="#7CC326" style={{ margin: '0 auto 16px' }} />
        <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>Application Received!</h3>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>Our partnerships team will reach out within 24 hours. Want to talk right away?</p>
        <a href="https://wa.me/919910059861?text=Hi%2C%20I%20just%20applied%20to%20the%20ChannelLoyalty%20partner%20program." target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ background: '#25D366', borderColor: '#25D366' }} data-testid="partner-apply-whatsapp">
          <MessageCircle size={18} /> WhatsApp Us Now
        </a>
      </div>
    );
  }

  var inputStyle = { width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', background: '#f8fafc' };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-8 max-w-lg mx-auto shadow-xl text-left" data-testid="partner-apply-form">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <input value={form.name} onChange={update('name')} placeholder="Your name *" required style={inputStyle} data-testid="partner-form-name" />
        <input value={form.company} onChange={update('company')} placeholder="Company *" required style={inputStyle} data-testid="partner-form-company" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <input type="email" value={form.email} onChange={update('email')} placeholder="Work email *" required style={inputStyle} data-testid="partner-form-email" />
        <input type="tel" value={form.phone} onChange={update('phone')} placeholder="Phone *" required style={inputStyle} data-testid="partner-form-phone" />
      </div>
      <textarea value={form.message} onChange={update('message')} placeholder="Tell us about your business (optional)" rows={3} style={Object.assign({}, inputStyle, { resize: 'vertical', marginBottom: '16px' })} data-testid="partner-form-message" />
      {status === 'error' && <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }} data-testid="partner-form-error">Something went wrong — please try again or WhatsApp us.</p>}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button type="submit" disabled={status === 'sending'} className="btn btn-primary" data-testid="partner-form-submit">
          {status === 'sending' ? 'Submitting...' : 'Apply Now'} <ArrowRight size={18} />
        </button>
        <a href="https://wa.me/919910059861?text=Hi%2C%20I%27m%20interested%20in%20the%20ChannelLoyalty%20partner%20program." target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{ color: '#1e293b', borderColor: '#e2e8f0' }} data-testid="partner-form-whatsapp">
          <MessageCircle size={18} /> WhatsApp Instead
        </a>
      </div>
    </form>
  );
}

export default function PartnerPage() {
  var [demoOpen, setDemoOpen] = useState(false);

  return (
    <div data-testid="partner-page">
      <Navbar onDemoClick={function() { setDemoOpen(true); }} />
      <SEOHead
        title="Partner With ChannelLoyalty - Channel Loyalty Partner Certification Program"
        description="Join ChannelLoyalty's partner ecosystem. Get certified, access co-branded materials, earn revenue share, and help enterprises transform their B2B channel loyalty programs."
        keywords="ChannelLoyalty partner program, loyalty platform partner, B2B loyalty certification, channel loyalty partner India"
        canonical="https://channelloyalty.ai/partners"
      />

      {/* Banner Hero */}
      <section className="hero-banner-full">
        <div className="hero-banner-carousel">
          <div className="hero-banner-track hero-banner-track--crop">
            <div className="hero-banner-slide active">
              <img src="/images/banner_partners.jpg" alt="ChannelLoyalty Partner Program - Grow With ChannelLoyalty" />
            </div>
          </div>
        </div>
      </section>

      {/* Partner Tiers */}
      <section className="section-light section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Partner Tiers</span>
            <h2>Three Tiers. One Mission.</h2>
            <p>Progress through our partner program as you grow your ChannelLoyalty practice.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {PARTNER_TIERS.map(function(tier, idx) {
              return <PartnerTierCard key={idx} tier={tier} idx={idx} />;
            })}
          </div>
        </div>
      </section>

      {/* Certification Curriculum */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Certification</span>
            <h2>ChannelLoyalty Partner Certification</h2>
            <p>31-hour comprehensive program covering platform, strategy, and industry specialization.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {CERT_MODULES.map(function(mod, idx) {
              var Icon = mod.icon;
              return (
                <div key={idx} className="card" style={{ padding: '28px' }} data-testid={"cert-module-" + idx}>
                  <Icon size={28} color="#2C39B8" />
                  <h4 style={{ fontSize: '17px', fontWeight: 700, margin: '12px 0 8px', color: '#1e293b' }}>{mod.title}</h4>
                  <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{mod.desc}</p>
                  <span style={{ display: 'inline-block', marginTop: '12px', fontSize: '12px', fontWeight: 600, color: '#2C39B8', background: '#2C39B811', padding: '4px 10px', borderRadius: '100px' }}>{mod.duration}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container relative z-10 text-center">
          <h2>Ready to Partner?</h2>
          <p>Join India&apos;s fastest-growing B2B loyalty ecosystem. Fill in your details and our partnerships team will reach out within 24 hours.</p>
          <div style={{ marginTop: '32px' }}>
            <PartnerApplyForm />
          </div>
        </div>
      </section>

      <MegaFooter />
      <DemoModal isOpen={demoOpen} onClose={function() { setDemoOpen(false); }} />
    </div>
  );
}
